import type { IdState, ReceptionBoard, ReceptionStay } from '../../../../api/stays-hotel-client';
import type { StatusTone } from '../../../status-pill/status-pill';
import { addDays as addDayKeys, formatDayKey } from '../../../../function-utils/building-time';

/**
 * A hotel's reception (Independent Stays IS9.6): the pure parts — which tab
 * holds what, what each stay's pills say, and which one-tap action a row
 * offers. The rules are the server's (BE src/stays/reception.js); this only
 * words them.
 */

export type ReceptionTab = 'arrivals' | 'in_house' | 'departures';

export const TABS: { key: ReceptionTab; label: string }[] = [
  { key: 'arrivals', label: 'Arrivals' },
  { key: 'in_house', label: 'In house' },
  { key: 'departures', label: 'Departures' },
];

export const rowsOf = (board: ReceptionBoard, tab: ReceptionTab): ReceptionStay[] =>
  tab === 'arrivals' ? board.arrivals : tab === 'in_house' ? board.in_house : board.departures;

export const countOf = (board: ReceptionBoard, tab: ReceptionTab): number => rowsOf(board, tab).length;

/** Each person's ID in a word reception can act on (IS0's in-house check). */
export const ID_WORDS: Record<IdState, { tone: StatusTone; label: string }> = {
  not_given: { tone: 'neutral', label: 'No ID yet' },
  read: { tone: 'success', label: 'ID read' },
  needs_look: { tone: 'warning', label: 'Look at the ID' },
  checked: { tone: 'success', label: 'ID checked' },
  rescan_asked: { tone: 'alert', label: 'New scan asked' },
};

/** The stay's online check-in in one pill. */
export const checkinPill = (s: ReceptionStay): { tone: StatusTone; label: string } => {
  if (s.checkin.complete) return { tone: 'success', label: 'Online check-in done' };
  if (s.checkin.link_opened) return { tone: 'warning', label: 'Check-in started' };
  if (s.checkin.link_sent) return { tone: 'neutral', label: 'Link sent, not opened' };

  return { tone: 'neutral', label: 'No online check-in yet' };
};

export const guestName = (s: { first_name: string; last_name: string }): string =>
  [s.first_name, s.last_name].filter(Boolean).join(' ') || 'Guest';

export const partyLine = (s: ReceptionStay): string => {
  const people = s.party_size === 1 ? '1 guest' : `${s.party_size} guests`;
  const nights = s.nights === 1 ? '1 night' : `${s.nights} nights`;

  return `${people} · ${nights}`;
};

export const roomLine = (s: ReceptionStay): string => {
  const type = s.room_type_name || 'Room';

  return s.room ? `${type} · Room ${s.room.label}` : `${type} · no room yet`;
};

export type RowAct = 'give_room' | 'check_in' | 'check_out' | null;

/** The one main action of a row on a tab (the server re-checks every one). */
export const mainAction = (s: ReceptionStay, tab: ReceptionTab): RowAct => {
  if (tab === 'arrivals') {
    if (s.status !== 'confirmed') return null;

    return s.room_assigned ? 'check_in' : 'give_room';
  }
  if (s.status === 'checked_in' && (tab === 'departures' || tab === 'in_house')) return 'check_out';

  return null;
};

export const ACT_LABEL: Record<Exclude<RowAct, null>, string> = {
  give_room: 'Give a room',
  check_in: 'Checked in',
  check_out: 'Checked out',
};

/** "Thu 29 Oct" for a property date key (the date is the property's: no zone shift). */
export const dayLabel = (day: string): string =>
  formatDayKey(day, { weekday: 'short', day: 'numeric', month: 'short' });

/** A property date key plus n days. */
export const addDays = (day: string, n: number): string => addDayKeys(day, n);

export const WALK_IN_NIGHTS_MAX = 30;
